<template>
    <section class="section-frame">
        <header v-if="showHeader" class="section-frame__header">
            <slot name="title" :title="title">
                <span class="section-frame__title">{{ title }}</span>
            </slot>

            <slot
                v-if="showMore"
                name="more"
                :url="moreUrl"
                :text="moreText"
                :icon-name="moreIconName"
            >
                <a v-if="moreUrl" class="section-frame__more" :href="moreUrl">
                    <span>{{ moreText }}</span>
                    <svg-icon
                        :icon-name="moreIconName"
                        :width="12"
                        :height="12"
                        class="section-frame__more-icon"
                    ></svg-icon>
                </a>
            </slot>
        </header>

        <div
            class="section-frame__body"
            :class="{
                'is-no-sidebar': !isSidebarVisible,
                'is-sidebar-right': isSidebarVisible && isSidebarRight,
            }"
        >
            <slot v-if="isSidebarVisible" name="sidebar">
                <gh-sidebar
                    :items="sidebarItems"
                    :active-id="sidebarActiveId"
                    :badge-variant="sidebarBadgeVariant"
                    :auto-scroll-active="sidebarAutoScrollActive"
                    :active-scroll-behavior="sidebarActiveScrollBehavior"
                    @change-item="handleSidebarChange"
                ></gh-sidebar>
            </slot>

            <div class="section-frame__content">
                <slot
                    v-if="hasContentSlot && normalizedItems.length > 0"
                    name="content"
                    :items="normalizedItems"
                ></slot>

                <template v-else>
                    <template v-if="normalizedItems.length > 0">
                        <div
                            v-for="(item, index) in normalizedItems"
                            :key="index"
                            class="section-frame__item"
                        >
                            <slot
                                name="item"
                                :item="item"
                                :index="index"
                            ></slot>
                        </div>
                    </template>

                    <slot
                        v-else
                        name="empty"
                        :empty-slot-props="emptySlotProps"
                    >
                        <button
                            v-if="showEmptyRetry"
                            type="button"
                            class="section-frame__empty-retry"
                            @click="handleRetry"
                        >
                            <svg-icon
                                icon-name="refresh-regular"
                                :width="32"
                                :height="32"
                                class="section-frame__empty-retry-icon"
                            ></svg-icon>
                        </button>
                        <p class="section-frame__empty">
                            <template
                                v-if="emptyState === SectionEmptyState.Loading"
                            >
                                <span>讀取中</span>
                                <span
                                    class="section-frame__loading-dots"
                                ></span>
                            </template>
                            <template v-else>
                                {{ resolvedEmptyText }}
                            </template>
                        </p>
                    </slot>
                </template>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
    import { computed, useSlots } from 'vue';
    import GhSidebar from '@/components/base/GhSidebar.vue';
    import SvgIcon from '@/components/base/SvgIcon.vue';
    import {
        BadgeVariant,
        ScrollBehaviorMode,
        SectionEmptyState,
        SidebarPosition,
        type ComponentValueMap,
        type MenuItem,
        type SectionFrameSidebarChangePayload,
        type SidebarMenuPayload,
    } from '@/types/components/base-components.types';

    // ----- props -----
    const props = withDefaults(
        defineProps<{
            emptyText?: string;
            emptyState?: SectionEmptyState;
            emptyRetryText?: string;
            items?: ComponentValueMap[];
            moreIconName?: string;
            moreText?: string;
            moreUrl?: string;
            showHeader?: boolean;
            showMore?: boolean;
            showEmptyRetry?: boolean;
            showSidebar?: boolean;
            sidebarActiveId?: string | number;
            sidebarActiveScrollBehavior?: ScrollBehaviorMode;
            sidebarAutoScrollActive?: boolean;
            sidebarBadgeVariant?: BadgeVariant;
            sidebarContext?: string | number | ComponentValueMap;
            sidebarItems?: MenuItem[];
            sidebarPosition?: SidebarPosition;
            title?: string;
        }>(),
        {
            emptyText: '此分類暫無資料。',
            emptyState: SectionEmptyState.Default,
            emptyRetryText: '重新讀取',
            items: () => [],
            moreIconName: 'arrow-right-regular',
            moreText: '更多',
            moreUrl: '',
            showHeader: true,
            showMore: true,
            showEmptyRetry: false,
            showSidebar: false,
            sidebarActiveId: '',
            sidebarActiveScrollBehavior: ScrollBehaviorMode.Smooth,
            sidebarAutoScrollActive: false,
            sidebarBadgeVariant: BadgeVariant.Gray,
            sidebarContext: '',
            sidebarItems: () => [],
            sidebarPosition: SidebarPosition.Left,
            title: '',
        }
    );

    // ----- emits -----
    const emits = defineEmits<{
        (event: 'retry'): void;
        (
            event: 'change-sidebar',
            payload: SectionFrameSidebarChangePayload
        ): void;
    }>();

    // ----- attrs -----
    const slots = useSlots();

    // ----- computed -----
    const hasContentSlot = computed(() => {
        return Boolean(slots.content);
    });

    // 開啟 sidebar 且有 sidebar 資料時才渲染側欄
    const isSidebarVisible = computed(() => {
        return props.showSidebar && props.sidebarItems.length > 0;
    });

    const isSidebarRight = computed(() => {
        return props.sidebarPosition === SidebarPosition.Right;
    });

    // 避免 items 傳入非陣列造成渲染錯誤
    const normalizedItems = computed(() => {
        return Array.isArray(props.items) ? props.items : [];
    });

    // 根據 emptyState 回傳對應文案
    const resolvedEmptyText = computed(() => {
        if (props.emptyState === SectionEmptyState.Loading) {
            return '讀取中…';
        }

        if (props.emptyState === SectionEmptyState.Timeout) {
            return '讀取逾時，請重試。';
        }

        if (props.emptyState === SectionEmptyState.Error) {
            return '資料讀取失敗，請重試。';
        }

        return props.emptyText;
    });

    const emptySlotProps = computed(() => {
        return {
            state: props.emptyState,
            text: resolvedEmptyText.value,
            showRetry: props.showEmptyRetry,
            retryText: props.emptyRetryText,
            retry: handleRetry,
        };
    });

    // ----- methods -----
    const handleRetry = (): void => {
        emits('retry');
    };

    // 封裝 sidebar 切換事件，統一對外 payload
    const handleSidebarChange = (payload: SidebarMenuPayload): void => {
        const item = payload ? payload.item : null;
        const itemId = payload ? payload.itemId : '';

        emits('change-sidebar', {
            item,
            itemId,
            sidebarContext: props.sidebarContext,
        });
    };
</script>
